import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { forgotPassword, portalLogin } from "../api";
import { linkLocalDevice } from "../portal/linkLocalDevice";
import { Lock, Mail, AlertCircle, CheckCircle, ArrowLeft } from "lucide-react";
import { tokens, sx } from "../theme";
import { BRAND, FEATURES } from "../brand";

const API_URL = process.env.REACT_APP_API_URL || "";

const MicrosoftIcon = () => (
  <svg width="18" height="18" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
    <rect x="1"  y="1"  width="9" height="9" fill="#f25022"/>
    <rect x="11" y="1"  width="9" height="9" fill="#7fba00"/>
    <rect x="1"  y="11" width="9" height="9" fill="#00a4ef"/>
    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
  </svg>
);

// ─── Themed building blocks (local to this page) ─────────────────
const cardStyle = {
  background: tokens.surface.panel,
  border: `1px solid ${tokens.border.soft}`,
  borderRadius: 16,
  padding: 32,
  boxShadow: tokens.shadow.panel,
  position: "relative",
  overflow: "hidden",
};
const cardShine = {
  position: "absolute", top: 0, left: 0, right: 0, height: 1,
  background: tokens.shadow.cardSheen, pointerEvents: "none",
};
const inputBase = {
  width: "100%",
  background: tokens.surface.input,
  border: `1px solid ${tokens.border.mid}`,
  borderRadius: 8,
  padding: "10px 12px 10px 36px",
  color: tokens.text.primary,
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "inherit",
  transition: "border-color 0.15s, box-shadow 0.15s",
};
const labelStyle = { fontSize: 12, color: tokens.text.muted, fontWeight: 500, display: "block", marginBottom: 6 };
const linkStyle  = { background: "none", border: "none", color: tokens.accent.indigo2, fontSize: 12, padding: 0, cursor: "pointer", fontFamily: "inherit" };

const ErrorBanner = ({ children }) => (
  <div style={{
    background: "rgba(127,29,29,0.4)",
    border: `1px solid ${tokens.accent.red}`,
    borderRadius: 8, padding: "10px 14px", marginBottom: 20,
    display: "flex", alignItems: "center", gap: 8,
    color: tokens.accent.red2, fontSize: 13,
  }}>
    <AlertCircle size={15} /> {children}
  </div>
);

const SsoButton = ({ onClick }) => (
  <button type="button" onClick={onClick} style={{
    width: "100%", padding: "11px 16px", background: "#fff",
    border: `1px solid ${tokens.border.mid}`, borderRadius: 8,
    color: "#1a1a1a", fontSize: 14, fontWeight: 600,
    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
    marginBottom: 20, fontFamily: "inherit",
    transition: "transform 0.15s, box-shadow 0.15s",
    boxShadow: tokens.shadow.panel,
  }}
  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = tokens.shadow.panelHi; }}
  onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)";    e.currentTarget.style.boxShadow = tokens.shadow.panel; }}>
    <MicrosoftIcon />
    Sign in with Microsoft
  </button>
);

const Divider = () => (
  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
    <div style={{ flex: 1, height: 1, background: tokens.border.soft }} />
    <span style={{ fontSize: 11, color: tokens.text.deep, fontWeight: 500 }}>or sign in with email</span>
    <div style={{ flex: 1, height: 1, background: tokens.border.soft }} />
  </div>
);

const PrimaryButton = ({ disabled, children }) => (
  <button type="submit" disabled={disabled} style={{
    background: disabled ? tokens.surface.input : tokens.gradient.primary,
    color: "#fff", padding: "11px", borderRadius: 8, fontWeight: 700, fontSize: 14,
    marginTop: 4, opacity: disabled ? 0.7 : 1,
    cursor: disabled ? "default" : "pointer",
    border: "none", fontFamily: "inherit",
    boxShadow: disabled ? "none" : tokens.shadow.primaryGlow,
    transition: "transform 0.15s",
  }}
  onMouseEnter={e => { if (!disabled) e.currentTarget.style.transform = "translateY(-1px)"; }}
  onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; }}>
    {children}
  </button>
);

const InputWithIcon = ({ icon: Icon, ...props }) => (
  <div style={{ position: "relative" }}>
    <Icon size={15} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: tokens.text.deep, pointerEvents: "none" }} />
    <input {...props} style={inputBase}
      onFocus={e => { e.target.style.borderColor = tokens.border.indigoHi; e.target.style.boxShadow = `0 0 0 3px rgba(99,102,241,0.1)`; }}
      onBlur={e => { e.target.style.borderColor = tokens.border.mid; e.target.style.boxShadow = "none"; }}
    />
  </div>
);

// ─────────────────────────────────────────────────────────────────
//   Login page
// ─────────────────────────────────────────────────────────────────
export default function Login() {
  const { login, loginWithToken } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState("portal");         // "staff" | "portal" — Client Portal is the default
  const [view, setView] = useState("login");          // "login" | "forgot" | "forgot-sent"
  const [form, setForm] = useState({ email: "", password: "" });
  const [fpEmail, setFpEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // ── SSO callback handling (unchanged behavior) ──────────────
  useEffect(() => {
    const params      = new URLSearchParams(window.location.search);
    const token       = params.get("token");
    const slug        = params.get("slug");
    const portalError = params.get("error");
    const staffToken  = params.get("staff_token");
    const ssoError    = params.get("sso_error");

    if (portalError) {
      const msgs = {
        sso_failed:       "Microsoft sign-in was cancelled.",
        domain_not_found: "Your email domain is not linked to a portal. Contact your admin.",
        sso_error:        "Microsoft sign-in failed. Please try again.",
      };
      setMode("portal");
      setError(msgs[portalError] || "Sign-in failed. Please try again.");
      window.history.replaceState({}, "", "/login");
      return;
    }
    if (ssoError) {
      const msgs = {
        no_account: "No MIDTC staff account was found for that Microsoft identity.",
        sso_failed: "Microsoft sign-in was cancelled.",
        sso_error:  "Microsoft sign-in failed. Please try again.",
      };
      setMode("staff");
      setError(msgs[ssoError] || "Microsoft sign-in failed. Please try again.");
      window.history.replaceState({}, "", "/login");
      return;
    }
    if (staffToken) {
      window.history.replaceState({}, "", "/login");
      loginWithToken(staffToken)
        .then(() => navigate("/dashboard"))
        .catch(() => {
          setMode("staff");
          setError("Sign-in token was invalid. Please try again.");
        });
      return;
    }
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        const contact = {
          id: payload.id, email: payload.email,
          clientId: payload.client_id, portalRole: payload.portal_role,
        };
        localStorage.setItem("midtc_portal_token",   token);
        localStorage.setItem("midtc_portal_contact", JSON.stringify(contact));
        window.history.replaceState({}, "", "/login");
        linkLocalDevice(token).catch(() => {}); // best-effort: link this machine to the user
        navigate(slug ? `/clientportal/${slug}` : "/clientportal/dashboard");
      } catch {
        setMode("portal");
        setError("Sign-in token was invalid. Please try again.");
        window.history.replaceState({}, "", "/login");
      }
    }
  }, [navigate, loginWithToken]);

  const handleStaffSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(form.email, form.password);
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.error || "Login failed. Please try again.");
    } finally { setLoading(false); }
  };

  const handlePortalSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res = await portalLogin(form.email, form.password);
      const { token, portal_slug, contact } = res.data;
      localStorage.setItem("midtc_portal_token",   token);
      localStorage.setItem("midtc_portal_contact", JSON.stringify(contact));
      linkLocalDevice(token).catch(() => {}); // best-effort: link this machine to the user
      navigate(portal_slug ? `/clientportal/${portal_slug}` : "/clientportal/dashboard");
    } catch (err) {
      setError(err.response?.data?.error || "Login failed. Please try again.");
    } finally { setLoading(false); }
  };

  const handleStaffSSO     = () => { window.location.href = `${API_URL}/api/auth/m365/staff/login`; };
  const handleMicrosoftSSO = () => { window.location.href = `${API_URL}/api/auth/m365/portal/login?return=${encodeURIComponent("/login")}`; };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await forgotPassword(fpEmail.trim().toLowerCase());
      setView("forgot-sent");
    } catch {
      setError("Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  const switchMode = (m) => { setMode(m); setError(""); setForm({ email: "", password: "" }); };

  return (
    <div style={{
      minHeight: "100vh",
      background: `radial-gradient(circle at 20% -10%, rgba(99,102,241,0.08), transparent 50%),
                   radial-gradient(circle at 100% 100%, rgba(124,58,237,0.06), transparent 40%),
                   ${tokens.surface.page}`,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      <div style={{ width: "100%", maxWidth: 420, animation: "fade-in 0.3s ease-out" }}>

        {/* Logo / brand */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{
            width: 80, height: 80, margin: "0 auto 14px", borderRadius: 18,
            background: tokens.gradient.primary,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 0 32px rgba(99,102,241,0.45), inset 0 1px 0 rgba(255,255,255,0.2)",
            overflow: "hidden",
          }}>
            <img src={BRAND.logo_url} alt={BRAND.name} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 18 }}
              onError={e => { e.target.style.display = "none"; }} />
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: tokens.text.primary, letterSpacing: "-0.3px" }}>{BRAND.name}</h1>
          <p style={{ color: tokens.text.faint, fontSize: 13, marginTop: 4, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>{BRAND.tagline}</p>
        </div>

        {/* Tab toggle */}
        {view === "login" && (
          <div style={{
            display: "flex", background: tokens.surface.panel,
            border: `1px solid ${tokens.border.soft}`, borderRadius: 10, padding: 4, marginBottom: 20, gap: 4,
          }}>
            {[
              { key: "portal", label: "Client Portal" },
              { key: "staff",  label: "Staff Login" },
            ].map(({ key, label }) => {
              const active = mode === key;
              return (
                <button key={key} onClick={() => switchMode(key)} style={{
                  flex: 1, padding: "9px 0", borderRadius: 7, fontSize: 13, fontWeight: 600,
                  cursor: "pointer", transition: "all .15s", fontFamily: "inherit",
                  background: active ? tokens.gradient.primary : "transparent",
                  color:      active ? "#fff" : tokens.text.faint,
                  border: "none",
                  boxShadow: active ? "0 2px 8px rgba(99,102,241,0.4)" : "none",
                }}>
                  {label}
                </button>
              );
            })}
          </div>
        )}

        {/* ── STAFF LOGIN ─────────────────────────────────────── */}
        {view === "login" && mode === "staff" && (
          <div style={cardStyle}>
            <div style={cardShine}/>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: tokens.text.primary, marginBottom: 22 }}>Sign in to your account</h2>

            {error && <ErrorBanner>{error}</ErrorBanner>}

            {FEATURES.m365_sso && (<><SsoButton onClick={handleStaffSSO} /><Divider /></>)}

            <form onSubmit={handleStaffSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label style={labelStyle}>Email Address</label>
                <InputWithIcon icon={Mail} type="email" value={form.email}
                  onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                  placeholder="agent@example.com" required />
              </div>
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <span style={labelStyle}>Password</span>
                  <button type="button" onClick={() => { setView("forgot"); setError(""); setFpEmail(form.email); }} style={linkStyle}>
                    Forgot password?
                  </button>
                </div>
                <InputWithIcon icon={Lock} type="password" value={form.password}
                  onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                  placeholder="••••••••" required />
              </div>
              <PrimaryButton disabled={loading}>{loading ? "Signing in..." : "Sign In"}</PrimaryButton>
            </form>
          </div>
        )}

        {/* ── CLIENT PORTAL LOGIN ──────────────────────────────── */}
        {view === "login" && mode === "portal" && (
          <div style={cardStyle}>
            <div style={cardShine}/>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: tokens.text.primary, marginBottom: 6 }}>Client Portal</h2>
            <p style={{ fontSize: 13, color: tokens.text.faint, marginBottom: 22, lineHeight: 1.5 }}>
              Sign in with your work email to view and submit support tickets.
            </p>

            {error && <ErrorBanner>{error}</ErrorBanner>}

            {FEATURES.m365_sso && (<><SsoButton onClick={handleMicrosoftSSO} /><Divider /></>)}

            <form onSubmit={handlePortalSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label style={labelStyle}>Email Address</label>
                <InputWithIcon icon={Mail} type="email" value={form.email}
                  onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                  placeholder="you@company.com" required />
              </div>
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <span style={labelStyle}>Password</span>
                  <button type="button" onClick={() => { setView("forgot"); setError(""); setFpEmail(form.email); }} style={linkStyle}>
                    Forgot password?
                  </button>
                </div>
                <InputWithIcon icon={Lock} type="password" value={form.password}
                  onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                  placeholder="••••••••" required />
              </div>
              <PrimaryButton disabled={loading}>{loading ? "Signing in..." : "Access Portal"}</PrimaryButton>
            </form>

            <p style={{ textAlign: "center", fontSize: 12, color: tokens.text.deep, marginTop: 18 }}>
              Need access? Contact your {BRAND.name} administrator.
            </p>
          </div>
        )}

        {/* ── FORGOT PASSWORD ──────────────────────────────────── */}
        {view === "forgot" && (
          <div style={cardStyle}>
            <div style={cardShine}/>
            <button onClick={() => { setView("login"); setError(""); }} style={{
              background: "none", border: "none", color: tokens.text.faint,
              display: "flex", alignItems: "center", gap: 6, fontSize: 13, marginBottom: 20,
              cursor: "pointer", fontFamily: "inherit",
            }}>
              <ArrowLeft size={14} /> Back to sign in
            </button>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: tokens.text.primary, marginBottom: 8 }}>Reset your password</h2>
            <p style={{ color: tokens.text.faint, fontSize: 13, marginBottom: 24, lineHeight: 1.6 }}>
              Enter your email and we'll send you a reset link.
            </p>
            {error && <ErrorBanner>{error}</ErrorBanner>}
            <form onSubmit={handleForgot} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label style={labelStyle}>Email Address</label>
                <InputWithIcon icon={Mail} type="email" value={fpEmail}
                  onChange={e => setFpEmail(e.target.value)}
                  placeholder="your@email.com" required autoFocus />
              </div>
              <PrimaryButton disabled={loading}>{loading ? "Sending..." : "Send Reset Link"}</PrimaryButton>
            </form>
          </div>
        )}

        {/* ── FORGOT SENT ──────────────────────────────────────── */}
        {view === "forgot-sent" && (
          <div style={cardStyle}>
            <div style={cardShine}/>
            <div style={{ textAlign: "center", padding: "8px 0 16px" }}>
              <div style={{
                width: 56, height: 56, borderRadius: "50%",
                background: "rgba(52,211,153,0.12)", border: "2px solid rgba(52,211,153,0.4)",
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 16px",
                boxShadow: "0 0 22px rgba(52,211,153,0.2)",
              }}>
                <CheckCircle size={28} style={{ color: tokens.accent.green }} />
              </div>
              <h2 style={{ fontSize: 17, fontWeight: 700, color: tokens.text.primary, marginBottom: 8 }}>Check your inbox</h2>
              <p style={{ color: tokens.text.faint, fontSize: 13, lineHeight: 1.7 }}>
                If <span style={{ color: tokens.text.muted }}>{fpEmail}</span> is registered, you'll receive a reset link shortly.
              </p>
              <p style={{ color: tokens.text.deep, fontSize: 12, marginTop: 12 }}>
                The link expires in 24 hours. Check your spam folder if you don't see it.
              </p>
            </div>
            <button onClick={() => { setView("login"); setError(""); }} style={{
              ...sx.buttonGhost, width: "100%", justifyContent: "center", marginTop: 8,
            }}>
              Back to Sign In
            </button>
          </div>
        )}

        <p style={{ textAlign: "center", color: tokens.border.mid, fontSize: 12, marginTop: 24 }}>
          {BRAND.name} · {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
